/** Utilities for desktop (Electron) OAuth deep-link handling. */

const DEEP_LINK_SCHEME = 'assembly://';
const REDIRECT_ATTEMPTED_KEY = 'desktop_oauth_redirect_attempted';

// Amplify storage keys
// cSpell:disable-next-line
// NOTE: "ouath" is Amplify's actual key name — not our typo.
// cSpell:disable-next-line
const AMPLIFY_PKCE_KEY = 'ouath_pkce_key';
const AMPLIFY_OAUTH_STATE_KEY = 'oauth_state';

// localStorage backup keys for desktop OAuth
// cSpell:disable-next-line
// NOTE: "ouath" is Amplify's actual key name — not our typo.
// cSpell:disable-next-line
const DESKTOP_PKCE_KEY = 'desktop_ouath_pkce_key';
const DESKTOP_OAUTH_STATE_KEY = 'desktop_oauth_state';

// URL / deep link paths
const LOGIN_PATH = '/login';
const DEEP_LINK_LOGIN_PATH = 'login';

// Query param names
const CODE_PARAM = 'code';
const STATE_PARAM = 'state';
const DESKTOP_STATE_PARAM = 'desktop';

/**
 * Back up Amplify's CSRF state and PKCE verifier to localStorage so they
 * survive the loadURL navigation in the Electron main process.
 * federatedSignIn stores these synchronously before opening the URL.
 */
export const backupDesktopOAuthKeys = () => {
  const oauthState = window.sessionStorage.getItem(AMPLIFY_OAUTH_STATE_KEY);
  const pkceKey = window.sessionStorage.getItem(AMPLIFY_PKCE_KEY);
  if (oauthState) {
    window.localStorage.setItem(DESKTOP_OAUTH_STATE_KEY, oauthState);
  }
  if (pkceKey) {
    window.localStorage.setItem(DESKTOP_PKCE_KEY, pkceKey);
  }
};

/**
 * Decode the custom state portion from Amplify's state parameter.
 * Amplify format: `<random>-<hexEncodedCustomState>`
 */
function decodeCustomState(stateParam: string): string | null {
  const dashIndex = stateParam.indexOf('-');
  if (dashIndex === -1) return null;

  const hex = stateParam.substring(dashIndex + 1);
  if (hex.length === 0 || hex.length % 2 !== 0) return null;
  try {
    let decoded = '';
    for (let i = 0; i < hex.length; i += 2) {
      decoded += String.fromCharCode(parseInt(hex.substring(i, i + 2), 16));
    }
    return decoded;
  } catch {
    return null;
  }
}

/**
 * Check if the current page is a desktop OAuth callback in the browser.
 * If so, redirect to the assembly:// deep link and return true.
 *
 * Amplify encodes custom state as `<random>-<hex>` in the `state` query param.
 * We decode the hex portion and check for `desktop=true`. If present, this
 * callback originated from the desktop app and should be handed back via deep link.
 *
 * The auth code is single-use — if the browser's Amplify exchanges it first,
 * Electron's exchange will fail. So we MUST prevent Auth.configure() from
 * running in the browser when this is a desktop callback. The browser page
 * stays on the redirect page; the user can manually refresh to fall back
 * to browser-based auth if the desktop app isn't available.
 *
 * Must run before Auth.configure() to prevent Amplify from consuming the auth code.
 *
 * @returns true if this is a desktop OAuth callback (caller should skip Auth.configure)
 */
export function handleDesktopOAuthRedirect(): boolean {
  // Only run in the browser, not in Electron
  if (typeof window === 'undefined' || window.electronAPI?.isElectron) {
    return false;
  }

  // Only handle /login with code + state params
  if (window.location.pathname !== LOGIN_PATH) {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  const code = params.get(CODE_PARAM);
  const state = params.get(STATE_PARAM);

  if (!code || !state) {
    return false;
  }

  const customState = decodeCustomState(state);
  if (!customState) {
    return false;
  }

  // Check if this callback originated from the desktop app
  const stateParams = new URLSearchParams(customState);
  if (stateParams.get(DESKTOP_STATE_PARAM) !== 'true') {
    return false;
  }

  // If we already attempted the deep link redirect and it didn't work
  // (e.g., user manually refreshed because the app wasn't running),
  // fall through to let Amplify handle the code in the browser.
  if (window.sessionStorage.getItem(REDIRECT_ATTEMPTED_KEY)) {
    window.sessionStorage.removeItem(REDIRECT_ATTEMPTED_KEY);
    return false;
  }

  // Mark that we're attempting the redirect so a manual page refresh
  // falls through to browser-based auth.
  window.sessionStorage.setItem(REDIRECT_ATTEMPTED_KEY, 'true');

  // Redirect to the desktop app via deep link.
  // No automatic fallback — the auth code is single-use, so we can't
  // risk the browser exchanging it before Electron does.
  window.location.href = `${DEEP_LINK_SCHEME}${DEEP_LINK_LOGIN_PATH}${window.location.search}`;

  return true;
}

/** Detect if we're running in the Electron desktop app. */
export function isElectronApp(): boolean {
  return !!window?.electronAPI?.isElectron;
}

/**
 * In Electron, auth callbacks arrive via loadURL which may clear sessionStorage.
 * Restore Amplify's CSRF state and PKCE verifier from the localStorage backup
 * so the token exchange succeeds.
 *
 * Must be called before Auth.configure() so Amplify picks up the restored keys.
 */
export function restoreAmplifyFromLocalStorage(): void {
  if (!isElectronApp()) {
    return;
  }

  const params = new URLSearchParams(window.location.search);

  if (params.get(CODE_PARAM) && params.get(STATE_PARAM)) {
    const backupState = window.localStorage.getItem(DESKTOP_OAUTH_STATE_KEY);
    const backupPkce = window.localStorage.getItem(DESKTOP_PKCE_KEY);
    const existingState = window.sessionStorage.getItem(
      AMPLIFY_OAUTH_STATE_KEY,
    );
    const existingPkce = window.sessionStorage.getItem(AMPLIFY_PKCE_KEY);

    if (backupState && !existingState) {
      window.sessionStorage.setItem(AMPLIFY_OAUTH_STATE_KEY, backupState);
    }
    if (backupPkce && !existingPkce) {
      window.sessionStorage.setItem(AMPLIFY_PKCE_KEY, backupPkce);
    }

    window.localStorage.removeItem(DESKTOP_OAUTH_STATE_KEY);
    window.localStorage.removeItem(DESKTOP_PKCE_KEY);
  }
}
